import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Alert,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  CircularProgress,
  Button,
} from "@mui/material";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import api from "../../../services/api";

const UNIT_OPTIONS = [
  { value: "Pcs",  label: "Pcs" },
  { value: "Unit", label: "Unit" },
  { value: "Set",  label: "Set" },
  { value: "Pair", label: "Pair (Pasang)" },
];

const C = {
  primary:    "#2563EB", primaryBg:  "#EFF6FF",
  surface:    "#FFFFFF", surfaceAlt: "#F8FAFC",
  border:     "#E2E8F0", borderHov:  "#CBD5E1",
  text1:      "#0F172A", text2:      "#475569", text3: "#94A3B8",
  infoBdr:    "#BFDBFE",
};

const CONDITION_OPTIONS = [
  { value: "baik",         label: "Baik" },
  { value: "cukup_baik",   label: "Cukup Baik" },
  { value: "rusak_ringan", label: "Rusak Ringan" },
  { value: "rusak_berat",  label: "Rusak Berat" },
];

const STATUS_OPTIONS = [
  { value: "aktif",           label: "Aktif" },
  { value: "tidak_aktif",     label: "Tidak Aktif" },
  { value: "dipinjam",        label: "Dipinjam" },
  { value: "dalam_perbaikan", label: "Dalam Perbaikan" },
];

const inputBase = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", backgroundColor: C.surface,
    fontSize: "0.82rem", color: C.text1,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.borderHov },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.8rem", color: C.text3 },
  "& .MuiInputLabel-root.Mui-focused": { color: C.primary },
};

// Helper kalkulasi prefix lokal instan untuk responsivitas pengetikan 0ms
function getLocalPrefix(customPrefix, name) {
  if (customPrefix && customPrefix.trim()) {
    return customPrefix.toUpperCase().trim().replace(/[^A-Z0-9]/g, "");
  }
  if (!name || !name.trim()) return "";
  const clean = name.toLowerCase().trim();
  const known = {
    harddisk: "HD", "hard disk": "HD", hdd: "HD", ssd: "SSD",
    "power supply": "PS", mainboard: "MB", motherboard: "MB",
    komputer: "PC", pc: "PC", laptop: "LP", monitor: "MN",
    keyboard: "KB", mouse: "MS", printer: "PR", scanner: "SC",
    proyektor: "PJ", infocus: "PJ", switch: "SW", router: "RT",
    kursi: "KR", meja: "MJ", lemari: "LM", ac: "AC",
  };
  let code = null;
  for (const [k, v] of Object.entries(known)) {
    if (clean.includes(k)) { code = v; break; }
  }
  if (!code) {
    const words = name.trim().split(/[\s\-_]+/);
    if (words.length > 1) {
      code = words.map(w => w[0] || "").join("").toUpperCase().slice(0, 3);
    } else {
      code = name.replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 2);
    }
  }
  if (!code) code = "BR";
  const yearMatch = name.match(/\b20(\d{2})\b/);
  const year = yearMatch ? yearMatch[1] : String(new Date().getFullYear()).slice(-2);
  return `1${code}${year}`;
}

const INITIAL_FORM = {
  name: "",
  quantity: 30,
  unit: "Unit",
  prefix: "",
  start_number: 1,
  category: "",
  specification: "",
  description: "",
  condition: "baik",
  status: "aktif",
  room_id: "",
  purchase_date: "",
  purchase_price: "",
  note: "",
};

function BulkItemModal({ open, onClose, rooms = [], onSuccess }) {
  const [bulkForm, setBulkForm] = useState(INITIAL_FORM);
  const [previewCodes, setPreviewCodes] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState("");

  const handleClose = useCallback(() => {
    if (bulkSubmitting) return;
    setBulkForm(INITIAL_FORM);
    setPreviewCodes([]);
    setBulkError("");
    onClose?.();
  }, [bulkSubmitting, onClose]);

  // Kalkulasi preview lokal secara instan tanpa menunggu network
  const localPreview = useMemo(() => {
    if (!bulkForm.name.trim() || Number(bulkForm.quantity) <= 0) return [];
    const prefix = getLocalPrefix(bulkForm.prefix, bulkForm.name);
    const basePrefix = prefix.endsWith("000") ? prefix : prefix + "000";
    const start = Number(bulkForm.start_number) || 1;
    const qty = Math.min(Number(bulkForm.quantity) || 1, 500);

    const codes = [];
    for (let i = 0; i < qty; i++) {
      codes.push(`${basePrefix}${start + i}`);
    }
    return codes;
  }, [bulkForm.name, bulkForm.quantity, bulkForm.prefix, bulkForm.start_number]);

  // Tampilkan preview lokal langsung, lalu sinkronkan dengan database via debounce
  const activeCodes = previewCodes.length > 0 ? previewCodes : localPreview;

  useEffect(() => {
    if (!open || !bulkForm.name.trim() || Number(bulkForm.quantity) <= 0) {
      setPreviewCodes((prev) => (prev.length > 0 ? [] : prev));
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await api.get("/items/preview-codes", {
          params: {
            name: bulkForm.name,
            quantity: Math.min(Number(bulkForm.quantity) || 1, 500),
            prefix: bulkForm.prefix || undefined,
            start_number: Number(bulkForm.start_number) || 1,
          },
          signal: controller.signal,
        });
        if (res.data.success) {
          setPreviewCodes(res.data.codes || []);
        }
      } catch (err) {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError" && !controller.signal.aborted) {
          /* silent */
        }
      } finally {
        if (!controller.signal.aborted) {
          setPreviewLoading(false);
        }
      }
    }, 400);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, bulkForm.name, bulkForm.quantity, bulkForm.prefix, bulkForm.start_number]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setBulkForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const roomMenuItems = useMemo(() => {
    return rooms.map((r) => (
      <MenuItem key={r.id} value={String(r.id)}>
        {r.name} ({r.code})
      </MenuItem>
    ));
  }, [rooms]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!bulkForm.name.trim()) {
      setBulkError("Nama barang wajib diisi.");
      return;
    }
    const qty = Number(bulkForm.quantity);
    if (!qty || qty < 1) {
      setBulkError("Jumlah barang minimal 1.");
      return;
    }

    setBulkSubmitting(true);
    setBulkError("");

    try {
      const payload = {
        ...bulkForm,
        quantity: qty,
        start_number: Number(bulkForm.start_number) || 1,
      };
      if (!payload.room_id) delete payload.room_id;
      if (!payload.purchase_price) delete payload.purchase_price;
      if (!payload.purchase_date) delete payload.purchase_date;
      if (!payload.note) delete payload.note;

      const res = await api.post("/items/bulk-store", payload);
      setBulkForm(INITIAL_FORM);
      setPreviewCodes([]);
      setBulkError("");
      onSuccess?.(res.data.message || `Berhasil menambahkan ${qty} barang sekaligus!`);
    } catch (err) {
      const msg = err.response?.data?.message || "Gagal menambahkan barang sekaligus.";
      setBulkError(msg);
    } finally {
      setBulkSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      transitionDuration={{ enter: 120, exit: 90 }}
      disableRestoreFocus
      PaperProps={{
        sx: {
          borderRadius: "14px",
          boxShadow: "0 12px 36px rgba(0,0,0,0.18)",
          willChange: "transform, opacity",
        },
      }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              backgroundColor: C.primaryBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PlaylistAddIcon sx={{ fontSize: 22, color: C.primary }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: C.text1, lineHeight: 1.2 }}>
              Input Barang Sekaligus (Bulk)
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: C.text3, mt: 0.25 }}>
              Daftarkan sejumlah barang sekaligus dengan generate kode barang otomatis
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={() => !bulkSubmitting && onClose()}
          sx={{ color: C.text3, borderRadius: "6px", "&:hover": { backgroundColor: C.surfaceAlt } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box component="form" onSubmit={handleSubmit} sx={{ px: 3, pt: 2.5, pb: 2 }}>
        {bulkError && (
          <Alert severity="error" onClose={() => setBulkError("")} sx={{ mb: 2, borderRadius: "8px", fontSize: "0.82rem" }}>
            {bulkError}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Nama Barang */}
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              required
              label="Nama Barang"
              name="name"
              value={bulkForm.name}
              onChange={handleChange}
              size="small"
              placeholder="Contoh: Komputer PC, Kursi Kantor"
              sx={inputBase}
            />
          </Grid>

          {/* Jumlah */}
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              required
              label="Jumlah Barang"
              name="quantity"
              type="number"
              value={bulkForm.quantity}
              onChange={handleChange}
              size="small"
              inputProps={{ min: 1, max: 500 }}
              sx={inputBase}
              helperText="Maks. 500 unit sekaligus"
            />
          </Grid>

          {/* Satuan */}
          <Grid item xs={6} md={4}>
            <FormControl fullWidth size="small" sx={inputBase}>
              <InputLabel>Satuan</InputLabel>
              <Select name="unit" value={bulkForm.unit} label="Satuan" onChange={handleChange}>
                {UNIT_OPTIONS.map((u) => (
                  <MenuItem key={u.value} value={u.value}>
                    {u.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Prefix & Nomor Urut */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Prefix Kode Barang (Opsional)"
              name="prefix"
              value={bulkForm.prefix}
              onChange={handleChange}
              size="small"
              placeholder="Contoh: 1HD24 atau 1PC26"
              sx={inputBase}
              helperText="Format: 1 + Inisial + Tahun + 000 + Nomor (contoh: 1HD240001). Tanpa strip (-)"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nomor Mulai Urutan"
              name="start_number"
              type="number"
              value={bulkForm.start_number}
              onChange={handleChange}
              size="small"
              inputProps={{ min: 1 }}
              sx={inputBase}
              helperText="Mulai nomor urutan (default: 1 / nomor lanjut berikutnya)"
            />
          </Grid>

          {/* Live Preview Box Kode Barang */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: "10px",
                backgroundColor: C.primaryBg,
                border: `1px solid ${C.infoBdr}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 16, color: C.primary }} />
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: C.primary }}>
                    Preview Kode Barang Otomatis ({activeCodes.length || Number(bulkForm.quantity) || 0} {bulkForm.unit})
                  </Typography>
                </Box>
                {previewLoading && <CircularProgress size={14} sx={{ color: C.primary }} />}
              </Box>

              {activeCodes.length > 0 ? (
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                    <Chip
                      size="small"
                      label={`Kode Pertama: ${activeCodes[0]}`}
                      sx={{
                        backgroundColor: "#fff",
                        borderColor: C.infoBdr,
                        border: "1px solid",
                        fontWeight: 600,
                        fontFamily: "monospace",
                        fontSize: "0.78rem",
                      }}
                    />
                    <Typography sx={{ fontSize: "0.75rem", color: C.text3 }}>s/d</Typography>
                    <Chip
                      size="small"
                      label={`Kode Terakhir: ${activeCodes[activeCodes.length - 1]}`}
                      sx={{
                        backgroundColor: "#fff",
                        borderColor: C.infoBdr,
                        border: "1px solid",
                        fontWeight: 600,
                        fontFamily: "monospace",
                        fontSize: "0.78rem",
                      }}
                    />
                  </Box>

                  {/* Sampel Chip Kode */}
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                    {activeCodes.slice(0, 5).map((code) => (
                      <Chip
                        key={code}
                        label={code}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.72rem",
                          backgroundColor: "#fff",
                          borderColor: C.border,
                        }}
                      />
                    ))}
                    {activeCodes.length > 5 && (
                      <Typography sx={{ fontSize: "0.72rem", color: C.text2, fontStyle: "italic" }}>
                        + {activeCodes.length - 5} kode lainnya akan otomatis digenerate secara berurutan
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ fontSize: "0.75rem", color: C.text3 }}>
                  {bulkForm.name.trim()
                    ? "Menyiapkan preview kode barang..."
                    : "Ketik Nama Barang di atas untuk melihat preview kode barang yang akan digenerate."}
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Kategori & Ruangan */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Kategori"
              name="category"
              value={bulkForm.category}
              onChange={handleChange}
              size="small"
              placeholder="e.g. Elektronik, Komputer, Furnitur"
              sx={inputBase}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small" sx={inputBase}>
              <InputLabel>Ruangan / Lokasi Awal</InputLabel>
              <Select name="room_id" value={bulkForm.room_id} label="Ruangan / Lokasi Awal" onChange={handleChange}>
                <MenuItem value="">
                  <em>Tidak ada / Belum ditentukan</em>
                </MenuItem>
                {roomMenuItems}
              </Select>
            </FormControl>
          </Grid>

          {/* Kondisi & Status */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small" sx={inputBase}>
              <InputLabel>Kondisi Awal</InputLabel>
              <Select name="condition" value={bulkForm.condition} label="Kondisi Awal" onChange={handleChange}>
                {CONDITION_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small" sx={inputBase}>
              <InputLabel>Status Awal</InputLabel>
              <Select name="status" value={bulkForm.status} label="Status Awal" onChange={handleChange}>
                {STATUS_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Tanggal & Harga Beli Satuan */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tanggal Beli (opsional)"
              name="purchase_date"
              type="date"
              value={bulkForm.purchase_date}
              onChange={handleChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={inputBase}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Harga Beli per Satuan (Rp, opsional)"
              name="purchase_price"
              type="number"
              value={bulkForm.purchase_price}
              onChange={handleChange}
              size="small"
              inputProps={{ min: 0 }}
              sx={inputBase}
            />
          </Grid>

          {/* Spesifikasi & Catatan */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Spesifikasi (opsional)"
              name="specification"
              value={bulkForm.specification}
              onChange={handleChange}
              size="small"
              multiline
              rows={2}
              placeholder="Contoh: Core i5 12th Gen, RAM 16GB, SSD 512GB NVMe"
              sx={inputBase}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Catatan Tambahan (opsional)"
              name="note"
              value={bulkForm.note}
              onChange={handleChange}
              size="small"
              placeholder="Catatan pendaftaran barang massal"
              sx={inputBase}
            />
          </Grid>
        </Grid>

        <DialogActions sx={{ px: 0, pt: 3, pb: 1, gap: 1 }}>
          <Button
            onClick={() => onClose()}
            disabled={bulkSubmitting}
            sx={{
              textTransform: "none",
              color: C.text2,
              fontWeight: 500,
              borderRadius: "8px",
              border: `1px solid ${C.border}`,
              px: 2.5,
              "&:hover": { backgroundColor: C.surfaceAlt },
            }}
          >
            Batal
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={bulkSubmitting || !bulkForm.name.trim()}
            startIcon={bulkSubmitting ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : <PlaylistAddIcon />}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              backgroundColor: C.primary,
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(37,99,235,0.25)",
              "&:hover": { backgroundColor: "#1D4ED8" },
              "&:disabled": { backgroundColor: C.border },
            }}
          >
            {bulkSubmitting
              ? `Mendaftarkan ${bulkForm.quantity} Barang...`
              : `Daftarkan ${bulkForm.quantity || 1} ${bulkForm.unit} Sekaligus`}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default React.memo(BulkItemModal);
